import { Component, OnDestroy, OnInit, EventEmitter, Output, HostListener, ViewChild, ElementRef, Input } from '@angular/core';
import { RuleEngineService } from '../../services/rule-engine.service';
import { Subscription } from 'rxjs';
import { DataParserService } from '../../services/data-parser.service';
import { FileNode, Rule } from '../models/rule-engine';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import { HttpClient } from '@angular/common/http';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { O } from '@angular/cdk/keycodes';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-template-tree',
  templateUrl: './template-tree.component.html',
  styleUrls: ['./template-tree.component.scss'],
})
export class TemplateTreeComponent implements OnInit, OnDestroy {
  @Output() closeEvent = new EventEmitter<void>();
  @Input() deliveryTemplateKey: string = 'DEFAULT';
  //#region variables
  // fileName: string = '';
  jsonData: any;
  copyOfJsonData: any;
  jsonForMatTree: any;
  copyOfJsonForMatTree: any;
  uniqueId: number = 0;
  leafNode: any;
  pathExpression: string[] = [];
  listOfRules: Rule[] = [];
  currentRuleCardNumber?: number;
  allPathsForAutoSuggestion: string[] = [];
  enableJsonTree: boolean = false;
  selectedButton: string ='';
  isWaitingForFileSelection: boolean = false;
  isLoadingFromFileUpload: boolean = false;

  // private httpClient:HttpClient;
  private _getChildren = (node: FileNode) => node.children;
  private xmlData: any;
  private _ruleEngineService: RuleEngineService;
  private _ruleEngineServiceSubscription!: Subscription;
  private _dataParserService: DataParserService;
  private _dataParserServiceSubscription!: Subscription;
  private _matTreeJsonSubscription!: Subscription;
  @ViewChild('fileInputlhs', { static: false }) fileInput!: ElementRef;

  nestedDataSource!: MatTreeNestedDataSource<FileNode>;
  nestedTreeControl: NestedTreeControl<FileNode>;
  hasNestedChild = (_: number, node: FileNode) => {
    // Attributes have leaf: null - they use the purple template (non-expandable)
    if (node.leaf === null) {
      return false;
    }
    // All nodes (even empty ones) use the white template to distinguish from attributes
    // The arrow will be conditionally hidden in the template for empty nodes
    return node.children !== undefined;
  };

  hasChildren = (node: FileNode): boolean => {
    // Helper method to check if node actually has children to expand
    return Array.isArray(node.children) && node.children.length > 0;
  };
  
  //#endregion

  //#region constructor
  constructor(
    private toastr: ToastrService,
    ruleEngineService: RuleEngineService,
    dataParserService: DataParserService,
    httpClient: HttpClient,
    private ngxUiLoaderService: NgxUiLoaderService
  ) {
    this._ruleEngineService = ruleEngineService;
    this._dataParserService = dataParserService;
    this.nestedTreeControl = new NestedTreeControl<FileNode>(this._getChildren);
    this.nestedDataSource = new MatTreeNestedDataSource();
    // this.httpClient = httpClient;
  }
  //#endregion

  //#region life cycle hooks
  ngOnInit(): void {
    //this.getFileName();
    this._dataParserService.getJsonDataForMatTree();
    //this.assignTheJsonForMatTreeToMatTree();
    // this._dataParserService.loadAPData();
    // if ((this.deliveryTemplateKey || '').toUpperCase() === 'RESULT_OUTBOUND') {
    //   this.onClickResultOutbound();
    // } else {
    //   this.onClickAP();
    // }
    if (this.isResultOutboundTemplate) {
      this.onClickResultOutbound();
    } else {
      this.onClickAP();
    }
    this._matTreeJsonSubscription = this._dataParserService.$getMatTreeJson.subscribe({
      next: (data) => {
        this.jsonForMatTree = data;
        this.assignTheJsonForMatTreeToMatTree();
        // Stop loader if it was started from file upload (after tree is displayed)
        if (this.isLoadingFromFileUpload) {
          this.isLoadingFromFileUpload = false;
          this.ngxUiLoaderService.stop();
        }
      },
      error: () => {
        // Stop loader on error if it was started from file upload
        if (this.isLoadingFromFileUpload) {
          this.isLoadingFromFileUpload = false;
          this.ngxUiLoaderService.stop();
        }
        this.toastr.error("Unable to generate mat tree", "", {
          timeOut: 4000,
          positionClass: "toast-bottom-right",
        });
      }
    })

  }

  get isResultOutboundTemplate(): boolean {
    return (this.deliveryTemplateKey || '').toUpperCase() === 'RESULT_OUTBOUND';
  }

  ngOnDestroy(): void {
    if (this._ruleEngineServiceSubscription) {
      this._ruleEngineServiceSubscription.unsubscribe();
    }
    if (this._dataParserServiceSubscription) {
      this._dataParserServiceSubscription.unsubscribe();
    }
    this.selectedButton='';
  }
  //#endregion
  
  /**
   * Handles the upload button click - starts loader and opens file dialog
   */
  onUploadButtonClick() {
    this.selectedButton = 'upload';
    // Set flag to indicate we're waiting for file selection
    this.isWaitingForFileSelection = true;
    // Start loading spinner immediately when button is clicked
    this.ngxUiLoaderService.start();
    // Trigger the file input click to open the file dialog
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.click();
    }
  }

  /**
   * Handles window focus event - detects when user returns to window after file dialog
   */
  @HostListener('window:focus', ['$event'])
  onWindowFocus() {
    // If we're waiting for file selection and window regains focus, check if file was selected
    if (this.isWaitingForFileSelection) {
      // Use a small delay to check if change event has fired (which resets the flag)
      // If flag is still true after delay, user cancelled
      setTimeout(() => {
        // Double-check: if flag is still true AND no file in input, user cancelled
        if (this.isWaitingForFileSelection) {
          const hasFiles = (this.fileInput?.nativeElement?.files?.length ?? 0) > 0;
          if (!hasFiles) {
            // No file was selected (user cancelled), stop the loader
            this.isWaitingForFileSelection = false;
            this.ngxUiLoaderService.stop();
          }
          // If hasFiles is true, change event will fire and handle it - don't stop loader
        }
      }, 150);
    }
  }

  async onFileUpload(event: any) {
    // Reset the flag IMMEDIATELY to prevent focus handler from stopping loader
    this.isWaitingForFileSelection = false;
    
    this.selectedButton='upload'
     if (event.target.files?.length) {
       try {
         //setting the filename using the service's behavior subject and then that will emit this value to the templete-tree component
         this._ruleEngineService.setFileName(event.target.files[0].name);
         //reading the content of the file
         const fileContent = await this.readTheContentOfTheUploadedFile(event.target.files[0]);
         
         // Convert the uploaded file content to XML using the API
         this._ruleEngineService.convertToXml(fileContent).subscribe({
           next: (xmlData) => {
             // The API always returns XML, use it for further processing
             this.xmlData = xmlData;
             // Set flag to track that loader should stop when tree is displayed
             this.isLoadingFromFileUpload = true;
             //sending the xml data to the service to convert the json and the emit the value to the template-tree component
             this._dataParserService.parseXmlToJson(this.xmlData);
             // Loader will stop when tree is displayed via $getMatTreeJson subscription
           },
           error: (err) => {
             this.isLoadingFromFileUpload = false;
             this.ngxUiLoaderService.stop();
             this.toastr.error("Unable to convert file to XML", "", {
               timeOut: 4000,
               positionClass: "toast-bottom-right",
             });
             console.error("Error converting file to XML:", err);
           }
         });
       } catch (error) {
         this.ngxUiLoaderService.stop();
         this.toastr.error("Error reading uploaded file", "", {
           timeOut: 4000,
           positionClass: "toast-bottom-right",
         });
         console.error("Error reading file:", error);
       }
     } else {
       // No file was selected (user cancelled), stop the loader
       this.ngxUiLoaderService.stop();
     }
   }


   private async readTheContentOfTheUploadedFile(file: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = () =>{
        reject(new Error('Error reading file'));
      }
      reader.readAsText(file);
    });
  }

  // private getFileName() {
  //   this._ruleEngineServiceSubscription = this._ruleEngineService
  //     .getFileName()
  //     .subscribe({
  //       next: (fileName) => {
  //         this.fileName = fileName;
  //       },
  //       error: (err) => {
  //         this.toastr.error("Unable to get file name", "", {
  //           timeOut: 4000,
  //           positionClass: 'toast-bottom-right'
  //         });
  //       }
  //     });
  // }

  private assignTheJsonForMatTreeToMatTree() {
    this.nestedDataSource.data = this.jsonForMatTree;
    this.nestedTreeControl.dataNodes = this.jsonForMatTree;
    this.nestedTreeControl.expandAll();
  }

  // private buildFileTreeForMatTreeUsingRawJson(jsonData: any): FileNode[] {
  //   const result: FileNode[] = [];
    
  //   Object.keys(jsonData).forEach(key => {
  //     const value = jsonData[key];
  
  //     if (value === null || value === undefined) {
  //       // If value is null/undefined, create a node with a null leaf
  //       const node: FileNode = { key: key, uniqueId: this.uniqueId++, leaf: null };
  //       result.push(node);
  //     } else if (Array.isArray(value)) {
  //       // If the value is an array...
  //       if (value.length > 0 && typeof value[0] === 'object') {
  //         // Array of objects: flatten it.
  //         // For each item in the array, create a node with the same key.
  //         value.forEach(item => {
  //           const node: FileNode = { key: key, uniqueId: this.uniqueId++ };
  //           const children = this.buildFileTreeForMatTreeUsingRawJson(item);
  //           if (children.length > 0) {
  //             node.children = children;
  //           }
  //           result.push(node);
  //         });
  //       } else {
  //         // Array of primitives: create one node with children for each element.
  //         const node: FileNode = { key: key, uniqueId: this.uniqueId++ };
  //         node.children = value.map(item => ({
  //           key: key,
  //           uniqueId: this.uniqueId++,
  //           leaf: item
  //         }));
  //         result.push(node);
  //       }
  //     } else if (typeof value === 'object') {
  //       // If the value is an object (but not an array), recursively process it.
  //       const node: FileNode = { key: key, uniqueId: this.uniqueId++ };
  //       const children = this.buildFileTreeForMatTreeUsingRawJson(value);
  //       if (children.length > 0) {
  //         node.children = children;
  //       }
  //       result.push(node);
  //     } else {
  //       // If the value is a primitive, just create a leaf node.
  //       const node: FileNode = { key: key, uniqueId: this.uniqueId++, leaf: value };
  //       result.push(node);
  //     }
  //   });
    
  //   return result;
  // }
  




  onNodeClick(node: FileNode) {
    this.checkForLeafNodeForGeneratingPath(node);
    this.pathExpression = this.findPathExpression(
      this._dataParserService.jsonForMatTree,
      node.key,
      node.uniqueId
    );
    this.pathExpression?.pop();
    
    // Only add @ prefix for attributes (leaf === null), not for nodes
    if ((node as any).leaf === null) {
      // This is an attribute
      this.pathExpression.push(`@${node.key}`);
    } else {
      // This is a node (even if empty or has children)
      this.pathExpression.push(node.key);
    }
    
    // Always send the path for any clicked node (attributes, empty nodes, or nodes with children)
    // The path includes all ancestors up to and including the clicked node
    this.sendTheDataFromTemplateToCreateRulesComponent(this.pathExpression);
  }

  private checkForLeafNodeForGeneratingPath(node: FileNode) {
    if (!node?.children?.length) {
      this.leafNode = node;
    } else {
      this.leafNode = undefined;
    }
  }

  findPathExpression(
    jsonForMatTree: any,
    targetName: any,
    uniqueId: number,
    path = []
  ): any {
    for (const item of jsonForMatTree) {
      // Add the current item's name to the path
      const currentPath: any = [...path, item.key];

      // If the current item's name matches the target name, return the path
      if (item.key === targetName && item.uniqueId == uniqueId) {
        return currentPath;
      }

      // If the current item has children, search within the children
      if (item.children) {
        const result = this.findPathExpression(
          item.children,
          targetName,
          uniqueId,
          currentPath
        );
        if (result) {
          return result;
        }
      }
    }

    // Return null if the name is not found in the current branch
    return null;
  }

  // private getJsonDataForMatTree(): any {
  //   this._dataParserServiceSubscription = this._dataParserService
  //     .$getJsonDataForMatTree()
  //     .subscribe({
  //       next: (jsonData) => {
  //         if(jsonData !== null){
  //           this.jsonData = jsonData;
  //           this.copyOfJsonData = structuredClone(this.jsonData);
  //           this.enableTree();
  //         }          
  //       },
  //       error: (err) => {this.toastr.error("Unable to generate mat tree", "", {
  //         timeOut: 4000,
  //         positionClass: 'toast-bottom-right'
  //       });}
  //     });
  // }

  // private enableTree() {
  //   if (this.jsonData) {
  //     this.jsonForMatTree = this.buildFileTreeForMatTreeUsingRawJson(
  //       this.jsonData
  //     );
  //     this.copyOfJsonForMatTree = structuredClone(this.jsonForMatTree);
  //     this.assignTheJsonForMatTreeToMatTree();
  //     this._dataParserService.setNodePaths(
  //       this.collectAllPaths(this.jsonForMatTree)
  //     );
  //     this._dataParserService.setNodesWithTheirLeafNodes(
  //       this.collectNodesWithLeaves(this.jsonForMatTree)
  //     );
  //   }
  // }

  sendTheDataFromTemplateToCreateRulesComponent(pathExpression: any) {
    this._ruleEngineService.setPath(pathExpression);
  }
  // collectAllPaths(jsonForMatTree: any, path = [], paths: string[] = []): any {
  //   for (const item of jsonForMatTree) {
  //     // Add the current item's name to the path
  //     const currentPath: any = [...path, item.key];

  //     // If the current item's name matches the target name, return the path
  //     // if (item.key === targetName && item.uniqueId == uniqueId) {
  //     //   return currentPath;
  //     // }
  //     if (!item.children) {
  //       const modifiedPath = [...path, `@${item.key}`].join('/');
  //       paths.push(modifiedPath);
  //     }

  //     // If the current item has children, search within the children
  //     if (item.children) {
  //       paths.push(...this.collectAllPaths(item.children, currentPath));
  //     }
  //   }
  //   return paths;

  // }

  // collectNodesWithLeaves(tree: FileNode[]): { nodeName: string, leafNodes: string[] }[] {
  //   const nodesWithLeaves: { nodeName: string, leafNodes: string[] }[] = [];
  
  //   // Recursive function to traverse the tree
  //   const traverse = (treeNodes: FileNode[], currentPath: string[] = []) => {
  //     for (const node of treeNodes) {
  //       const currentPathWithNode = [...currentPath, node.key];
  
  //       if (node.children) {
  //         // Recursively process child nodes
  //         traverse(node.children, currentPathWithNode);
  //       } else {
  //         // Leaf node found
  //         const parentNodeName = currentPath.join('/');
  //         const leafNode = `@${node.key}`;
  
  //         // Add leaf to its parent's entry or create a new one
  //         const parentNode = nodesWithLeaves.find(n => n.nodeName === parentNodeName);
  //         if (parentNode) {
  //           parentNode.leafNodes.push(leafNode);
  //         } else {
  //           nodesWithLeaves.push({ nodeName: parentNodeName, leafNodes: [leafNode] });
  //         }
  //       }
  //     }
  //   };
  
  //   traverse(tree);
  
  //   return nodesWithLeaves;
  // }  

  public onClickAP() {
    this.selectedButton = 'AP';
    this._dataParserService.loadAPData();
  }


  public onClickCP() {
    this.selectedButton = 'CP';
    this._dataParserService.loadCPData();
  }

  public onClickResultOutbound() {
    this.selectedButton = 'AP';
    this._dataParserService.loadResultOutboundData();
  }

  onTemplateClose(){
    this.closeEvent.emit();
  }
}
